import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Rating,
  Stack,
} from '@mui/material';
import {
  Feedback as FeedbackIcon,
  Send,
  ThumbUp,
  ThumbDown,
  BugReport,
  EmojiObjects,
  Star,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import FeatureGuard from '../components/FeatureGuard';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

interface FeedbackFormData {
  type: 'feedback' | 'suggestion' | 'bug' | 'feature';
  category: string;
  title: string;
  description: string;
  rating?: number;
  priority: 'low' | 'medium' | 'high';
  sentiment: 'positive' | 'neutral' | 'negative';
}

const FeedbackPage: React.FC = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [formData, setFormData] = useState<FeedbackFormData>({
    type: 'feedback',
    category: '',
    title: '',
    description: '',
    rating: undefined,
    priority: 'medium',
    sentiment: 'neutral',
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const feedbackTypes = [
    {
      value: 'feedback',
      label: 'General Feedback',
      icon: <FeedbackIcon />,
      description: 'Share your thoughts about the platform',
      color: 'primary',
    },
    {
      value: 'suggestion',
      label: 'Feature Suggestion',
      icon: <EmojiObjects />,
      description: 'Suggest new features or improvements',
      color: 'info',
    },
    {
      value: 'bug',
      label: 'Bug Report',
      icon: <BugReport />,
      description: 'Report issues or problems you encountered',
      color: 'error',
    },
    {
      value: 'feature',
      label: 'Feature Request',
      icon: <Star />,
      description: 'Request specific functionality',
      color: 'warning',
    },
  ];

  const categories = {
    feedback: ['User Interface', 'Performance', 'Usability', 'Content', 'Support', 'General'],
    suggestion: ['Trading Tools', 'Education', 'Community Features', 'Analytics', 'Mobile App', 'API', 'Other'],
    bug: ['Login/Authentication', 'Trading Journal', 'Charts', 'Data Loading', 'Mobile Issues', 'Browser Issues', 'Other'],
    feature: ['Trading Features', 'Analysis Tools', 'Notifications', 'Reporting', 'Integration', 'Mobile Features', 'Other'],
  };

  const handleTypeChange = (type: string) => {
    setFormData(prev => ({
      ...prev,
      type: type as FeedbackFormData['type'],
      category: '', // Reset category when type changes
    }));
  };

  const handleChange = (field: keyof FeedbackFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim() || !formData.category) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      const feedbackData: any = {
        type: formData.type,
        category: formData.category,
        title: formData.title.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        sentiment: formData.sentiment,
        timestamp: serverTimestamp(),
        status: 'open',
        votes: {
          up: 0,
          down: 0,
          total: 0,
        },
      };

      // Only include rating if it has a value (for feedback type)
      if (formData.rating && formData.rating > 0) {
        feedbackData.rating = formData.rating;
      }

      // Include user information if authenticated
      if (currentUser) {
        feedbackData.userId = currentUser.uid;
        feedbackData.userEmail = currentUser.email;
        feedbackData.userName = currentUser.displayName || 'Anonymous';
      } else {
        feedbackData.userName = 'Anonymous';
      }

      await addDoc(collection(db, 'feedback'), feedbackData);

      setSuccess(true);
      setFormData({
        type: 'feedback',
        category: '',
        title: '',
        description: '',
        rating: undefined,
        priority: 'medium',
        sentiment: 'neutral',
      });
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000);
    } catch (error: any) {
      setError('Failed to submit feedback. Please try again.');
      console.error('Error submitting feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 10, md: 12 }, pb: 8 }}>
      <Container maxWidth="lg">
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header Section */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                component="h1"
                gutterBottom
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 800,
                  mb: 3,
                }}
              >
                Feedback & Suggestions
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: '600px', mx: 'auto', mb: 2 }}
              >
                Help us improve! Share your feedback, suggestions, or report issues to make our platform better.
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: '500px', mx: 'auto' }}
              >
                Your input is valuable in shaping the future of our trading platform.
              </Typography>
            </Box>
          </motion.div>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 6 }}>
            {/* Feedback Form */}
            <Box sx={{ flex: { xs: '1', md: '2' } }}>
              <motion.div variants={itemVariants}>
                <MotionCard
                  variants={itemVariants}
                  sx={{
                    p: { xs: 3, md: 4 },
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 215, 0, 0.2)',
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h4"
                      component="h2"
                      gutterBottom
                      sx={{ 
                        color: 'primary.main',
                        fontWeight: 700,
                        mb: 3,
                      }}
                    >
                      Share Your Thoughts
                    </Typography>

                    {success && (
                      <Alert severity="success" sx={{ mb: 3 }}>
                        Thank you for your feedback! We've received your submission and will review it soon.
                      </Alert>
                    )}

                    {error && (
                      <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                      </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit}>
                      {/* Feedback Type Selection */}
                      <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" gutterBottom>
                          What would you like to share? *
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                          {feedbackTypes.map((type) => (
                            <Card
                              key={type.value}
                              sx={{
                                cursor: 'pointer',
                                border: formData.type === type.value 
                                  ? `2px solid ${theme.palette.primary.main}` 
                                  : '1px solid rgba(255, 255, 255, 0.1)',
                                backgroundColor: formData.type === type.value 
                                  ? 'rgba(255, 215, 0, 0.1)' 
                                  : 'transparent',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 215, 0, 0.05)',
                                  transform: 'translateY(-2px)',
                                },
                              }}
                              onClick={() => handleTypeChange(type.value)}
                            >
                              <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <Box sx={{ color: `${type.color}.main`, mr: 1 }}>
                                    {type.icon}
                                  </Box>
                                  <Typography variant="subtitle1" fontWeight={600}>
                                    {type.label}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {type.description}
                                </Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </Box>

                      <Stack spacing={3}>
                        {/* Category and Priority Selection */}
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                          <FormControl fullWidth required>
                            <InputLabel>Category</InputLabel>
                            <Select
                              value={formData.category}
                              label="Category"
                              onChange={(e) => handleChange('category', e.target.value)}
                            >
                              {categories[formData.type].map((category) => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl fullWidth>
                            <InputLabel>Priority</InputLabel>
                            <Select
                              value={formData.priority}
                              label="Priority"
                              onChange={(e) => handleChange('priority', e.target.value)}
                            >
                              <MenuItem value="low">Low</MenuItem>
                              <MenuItem value="medium">Medium</MenuItem>
                              <MenuItem value="high">High</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Title */}
                        <TextField
                          fullWidth
                          label="Title"
                          value={formData.title}
                          onChange={(e) => handleChange('title', e.target.value)}
                          required
                          placeholder="Brief summary of your feedback or suggestion"
                        />

                        {/* Rating (for feedback type) */}
                        {formData.type === 'feedback' && (
                          <Box>
                            <Typography variant="subtitle1" gutterBottom>
                              Overall Rating (Optional)
                            </Typography>
                            <Rating
                              value={formData.rating || 0}
                              onChange={(_, value) => handleChange('rating', value)}
                              size="large"
                            />
                          </Box>
                        )}

                        {/* Sentiment */}
                        <FormControl component="fieldset">
                          <FormLabel component="legend">Overall Sentiment</FormLabel>
                          <RadioGroup
                            row
                            value={formData.sentiment}
                            onChange={(e) => handleChange('sentiment', e.target.value)}
                          >
                            <FormControlLabel
                              value="positive"
                              control={<Radio />}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <ThumbUp sx={{ mr: 0.5, color: 'success.main' }} />
                                  Positive
                                </Box>
                              }
                            />
                            <FormControlLabel
                              value="neutral"
                              control={<Radio />}
                              label="Neutral"
                            />
                            <FormControlLabel
                              value="negative"
                              control={<Radio />}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <ThumbDown sx={{ mr: 0.5, color: 'error.main' }} />
                                  Negative
                                </Box>
                              }
                            />
                          </RadioGroup>
                        </FormControl>

                        {/* Description */}
                        <TextField
                          fullWidth
                          label="Description"
                          value={formData.description}
                          onChange={(e) => handleChange('description', e.target.value)}
                          required
                          multiline
                          rows={6}
                          placeholder="Provide detailed information about your feedback, suggestion, or issue..."
                        />

                        {/* Submit Button */}
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={loading}
                          startIcon={<Send />}
                          sx={{
                            px: 4,
                            py: 1.5,
                            fontSize: '1.1rem',
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            color: '#000000',
                            fontWeight: 700,
                            '&:hover': {
                              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, #996F08)`,
                              transform: 'translateY(-2px)',
                            },
                            '&:disabled': {
                              background: 'rgba(255, 215, 0, 0.3)',
                              color: 'rgba(0, 0, 0, 0.5)',
                            },
                          }}
                        >
                          {loading ? 'Submitting...' : 'Submit Feedback'}
                        </Button>
                      </Stack>
                    </Box>
                  </CardContent>
                </MotionCard>
              </motion.div>
            </Box>

            {/* Information Panel */}
            <Box sx={{ flex: { xs: '1', md: '1' } }}>
              <motion.div variants={itemVariants}>
                <Stack spacing={3}>
                  {/* Feedback Types Info */}
                  <MotionCard
                    variants={itemVariants}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 215, 0, 0.1)',
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{ 
                          color: 'primary.main',
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        How We Use Your Feedback
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" color="primary">
                            💡 Suggestions & Features
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Help us prioritize new features and improvements
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="error">
                            🐛 Bug Reports
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Critical for maintaining platform stability
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="info">
                            📊 General Feedback
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Helps us understand user experience
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </MotionCard>

                  {/* Response Time Info */}
                  <MotionCard
                    variants={itemVariants}
                    sx={{
                      background: 'rgba(255, 215, 0, 0.1)',
                      border: '1px solid rgba(255, 215, 0, 0.3)',
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{ 
                          color: 'primary.main',
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Response Timeline
                      </Typography>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Bug Reports:</Typography>
                          <Chip label="24-48 hours" size="small" color="error" />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Feature Requests:</Typography>
                          <Chip label="1-2 weeks" size="small" color="info" />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">General Feedback:</Typography>
                          <Chip label="3-5 days" size="small" color="warning" />
                        </Box>
                      </Stack>
                    </CardContent>
                  </MotionCard>

                  {/* Tips */}
                  <MotionCard
                    variants={itemVariants}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 215, 0, 0.1)',
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{ 
                          color: 'primary.main',
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Tips for Better Feedback
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          • Be specific about the issue or suggestion
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Include steps to reproduce bugs
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Mention your browser/device when relevant
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Explain the expected vs actual behavior
                        </Typography>
                      </Stack>
                    </CardContent>
                  </MotionCard>
                </Stack>
              </motion.div>
            </Box>
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
};

function FeedbackFeature() {
  return (
    <FeatureGuard featureId="feedback">
      <FeedbackPage />
    </FeatureGuard>
  );
}

export const Feedback = FeedbackFeature;

export default FeedbackFeature; 